import React from "react";
import {
  BedDouble,
  Users,
  MapPin,
  Maximize,
  ArrowUpRight,
  Home,
} from "lucide-react";
import { PROPERTY_IMAGES } from "@/lib/propertyImages";
import { useReveal } from "@/hooks/useReveal";

const properties = [
  {
    img: PROPERTY_IMAGES.livingRoomWhiteBuiltIns,
    location: "Alexandria, VA",
    type: "Alexandria Mansion",
    beds: 6,
    guests: 12,
    area: "4,000 sqft",
    span: "lg:col-span-2 lg:row-span-2",
    tall: true,
  },
  {
    img: PROPERTY_IMAGES.livingRoom,
    location: "Alexandria, VA",
    type: "Game Room Estate",
    beds: 5,
    guests: 10,
    area: "2,500 sqft",
    span: "",
  },
  {
    img: PROPERTY_IMAGES.kitchen,
    location: "Alexandria, VA",
    type: "Spacious Family Home",
    beds: 5,
    guests: 10,
    area: "2,600 sqft",
    span: "",
  },
  {
    img: PROPERTY_IMAGES.gameRoom,
    location: "Alexandria, VA",
    type: "Entertainer's Retreat",
    beds: 5,
    guests: 10,
    area: "2,800 sqft",
    span: "",
  },
  {
    img: PROPERTY_IMAGES.livingRoomFireplace,
    location: "Alexandria, VA",
    type: "Family Home",
    beds: 5,
    guests: 10,
    area: "2,500 sqft",
    span: "",
  },
  {
    img: PROPERTY_IMAGES.familyRoomBrick,
    location: "Alexandria, VA",
    type: "Old Town Mansion",
    beds: 6,
    guests: 16,
    area: "4,800 sqft",
    span: "",
  },
  {
    img: PROPERTY_IMAGES.livingRoomCurvedSofa,
    location: "Alexandria, VA",
    type: "Grand Estate",
    beds: 7,
    guests: 20,
    area: "5,000 sqft",
    span: "",
  },
  {
    img: PROPERTY_IMAGES.diningRoundMarble,
    location: "Alexandria, VA",
    type: "Open-Concept Home",
    beds: 6,
    guests: 10,
    area: "2,300 sqft",
    span: "",
  },
];

const collageImages = [
  PROPERTY_IMAGES.bedroom,
  PROPERTY_IMAGES.kitchenBright,
  PROPERTY_IMAGES.livingRoomDining,
  PROPERTY_IMAGES.sunroomSeating,
  PROPERTY_IMAGES.livingRoomBrick2,
  PROPERTY_IMAGES.bedroomCeilingFan,
  PROPERTY_IMAGES.openKitchenLiving,
  PROPERTY_IMAGES.loftLivingDining,
  PROPERTY_IMAGES.livingRoomBigTv,
];

const Portfolio = () => {
  const ref = useReveal<HTMLDivElement>();

  return (
    <section className="py-24 bg-background" ref={ref}>
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <span className="reveal text-xs font-bold uppercase tracking-[0.25em] text-primary">
            Our Portfolio
          </span>
          <h2 className="reveal reveal-delay-1 text-3xl md:text-5xl font-heading font-bold text-foreground">
            Properties We Manage
          </h2>
          <p className="reveal reveal-delay-2 text-lg text-muted-foreground">
            A glimpse of the Northern Virginia homes we operate as premium
            short-term rentals — professionally staged, guest-ready, and
            meticulously maintained.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 max-w-7xl mx-auto auto-rows-[220px] md:auto-rows-[260px]">
          {properties.map((p, i) => (
            <div
              key={i}
              className={`reveal reveal-delay-${(i % 4) + 1} group relative overflow-hidden rounded-2xl border border-border/60 shadow-sm hover:shadow-2xl hover:-translate-y-1 transition-all duration-500 ${p.span}`}
            >
              <img
                src={p.img}
                alt={`${p.type} in ${p.location} managed by JNN Homes`}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent transition-opacity duration-500 group-hover:from-black/90" />

              {/* Hover corner accent */}
              <div className="absolute top-4 right-4 w-9 h-9 rounded-full bg-background/90 backdrop-blur-sm flex items-center justify-center opacity-0 -translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
                <ArrowUpRight className="w-4 h-4 text-primary" />
              </div>

              <div className="absolute top-4 left-4">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-background/90 backdrop-blur-sm text-xs font-bold text-foreground">
                  <MapPin className="w-3 h-3 text-primary" />
                  {p.location}
                </span>
              </div>

              <div className="absolute bottom-0 left-0 right-0 p-5 space-y-2 translate-y-1 group-hover:translate-y-0 transition-transform duration-500">
                <h3
                  className={`font-heading font-bold text-white ${p.tall ? "text-2xl md:text-3xl" : "text-lg"}`}
                >
                  {p.type}
                </h3>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-white/90 text-xs">
                  <span className="inline-flex items-center gap-1">
                    <BedDouble className="w-3.5 h-3.5 text-primary" /> {p.beds}{" "}
                    Beds
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Users className="w-3.5 h-3.5 text-primary" /> {p.guests}{" "}
                    Guests
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Maximize className="w-3.5 h-3.5 text-primary" /> {p.area}
                  </span>
                </div>
              </div>
            </div>
          ))}

          {/* Thumbnail collage — "many more" */}
          <div className="reveal reveal-delay-4 group relative overflow-hidden rounded-2xl border border-primary/30 shadow-sm hover:shadow-2xl hover:-translate-y-1 transition-all duration-500">
            <div className="absolute inset-0 grid grid-cols-3 grid-rows-3 gap-0.5 p-0.5">
              {collageImages.map((img, i) => (
                <div key={i} className="relative overflow-hidden">
                  <img
                    src={img}
                    alt="JNN Homes managed property"
                    className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/20 transition-opacity duration-500 group-hover:from-black/95" />
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-4">
              <div className="w-12 h-12 rounded-full bg-primary/90 backdrop-blur-sm flex items-center justify-center mb-3 shadow-lg">
                <Home className="w-6 h-6 text-primary-foreground" />
              </div>
              <h3 className="font-heading font-bold text-white text-lg md:text-xl leading-tight">
                Many More
              </h3>
              <p className="text-white/80 text-xs mt-1">
                homes across Northern Virginia
              </p>
            </div>
          </div>
        </div>

        <div className="reveal flex flex-col items-center gap-3 mt-10">
          <div className="flex items-center gap-3">
            <span className="h-px w-8 bg-primary/40" />
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-sm font-bold text-primary">
              <Home className="w-4 h-4" />
              And many more homes across Northern Virginia
            </span>
            <span className="h-px w-8 bg-primary/40" />
          </div>
          <p className="text-center text-xs text-muted-foreground italic max-w-2xl">
            A selection of Alexandria homes currently managed by JNN Homes —
            with many more properties across Northern Virginia.
          </p>
        </div>
      </div>
    </section>
  );
};

export default Portfolio;
